import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import notificationSelectors from 'store/notification/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import './styles.css';
import moment from 'moment';
import notificationActions from 'store/notification/actions';
import { API, getDataCookie } from 'common';
import { PAGE_SIZE_DEFAULT } from '.';
import ic_clock from '../../assets/clock.svg';
import { useHistory } from 'react-router-dom';
import { AppointmentTypes } from 'views/notification/instance';
import type { Notification, NotificationListQuery } from 'types/models';


export default function NotificationPanel() {
  const notifications = useAppSelector(notificationSelectors.notifications);
  // REACT_APP_COOKIE_KEY is always defined at build time (mirrors common.ts's own usage of `!` on env keys).
  const staffId = getDataCookie(process.env.REACT_APP_COOKIE_KEY!);
  const _history = useHistory();
  const [params, setParams] = useState<NotificationListQuery>({
    page: 0,
    size: PAGE_SIZE_DEFAULT,
    staffId: staffId
  });
  const loaderRef = useRef<HTMLLIElement>(null);
  const dispatch = useAppDispatch();

  const fetchData = (params: NotificationListQuery) => {
    // non-null: only invoked once fetching has started, so notifications is expected to be populated
    if (notifications!.last) return;

    if(notifications!.page < params.page) return;

    setParams(prev => {
      dispatch(notificationActions.getNotifications.fetch({
        ...prev,
        page: prev.page + 1,
      }));
      return {
        ...prev,
        page: prev.page + 1
      };
    });
  };

  const markAsRead = async (notification: Notification) => {
    if(notification.read) return;

    dispatch(notificationActions.setNotifications({
      // non-null: only reachable once notifications has already been fetched (see filteredNotifications below)
      ...notifications!,
      content: notifications!.content.map(n => {
        if(n.id === notification.id) {
          return {
            ...n,
            read: true
          };
        }
        return n;
      })
    }));

    // non-null: assumed present for appointment-originated notifications, matching the original untyped access
    _history.push(`/appointment-staff/${notification.data!.appointmentId}`);

    try {
      const response = await API.put(`staff-notification/${notification.id}/read?staffId=${staffId}`);

      if (response.status == 200) {
        console.log('Mark as read success');
        dispatch(notificationActions.countNotificationUnread.fetch({staffId}));
      }
    }

    catch (error) {
      dispatch(notificationActions.setNotifications({
      ...notifications!,
      content: notifications!.content.map(n => {
        if(n.id === notification.id) {
          return {
            ...n,
            read: false
          };
        }
        return n;
      })
    }));
    }
  };

  const filteredNotifications = (notifications?.content ?? []).filter((n) => {
    return true;
  });

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchData(params);
      }
    }, { threshold: 1.0 });

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className='notification-panel'>
      {/* Header */}
      <div className='notification-header'>
          <h4>Notifications</h4>
      </div>

      {/* List */}
      <ul className='notification-list'>
        {filteredNotifications.length === 0 && (
          <li className='notification-empty'>No notifications</li>
        )}

        {filteredNotifications.map((notification) => {
          const tab = AppointmentTypes.find((t) => t.key === notification.notificationType);
          return <li onClick={() => markAsRead(notification)} key={notification.id} className={`notification-item ${!notification.read ? 'read' : ''}`}>
            <div className='notification-content'>
              <div className='notification-item-header'>
                <span className='notification-badge'>
                  {notification.notificationType === 'APPOINTMENT_CANCELED' ? 'Cancelled' : undefined}
                  {notification.notificationType === 'APPOINTMENT_REMINDER' ? 'Reminder' : undefined}
                </span>
                <div className='notification-meta-container'>
                  <img width={12} height={12} src={ic_clock} alt="clock" />
                  <p className='notification-meta'>{moment().format('MM-DD-YYYY HH:mm A') !== moment(notification.createDate).format('MM-DD-YYYY HH:mm A') ? moment(notification.createDate).format('MM-DD-YYYY HH:mm A') : 'Just now'}</p>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p className='notification-title'>{notification.title}</p>
                {!notification.read && (
                  <div className='dot'></div>
                )}

              </div>
                <p className='notification-body'>{notification.body}</p>
            </div>
          </li>
        })}
        {filteredNotifications.length > 0 && !notifications!.last && <li ref={loaderRef}>Loading more...</li>}
      </ul>
      <div className='width-full' style={{display: 'flex', justifyContent: 'center'}}>
        <a href='/notifications' className='notification-view-all'>View all</a>
      </div>
    </div>
  );
}
